import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, merge, Subscription } from 'rxjs';
import { InvalidControlScrollService } from '../../../core/services/invalid-control-scroll.service';

@Component({
    selector: 'app-validation-error-summary',
    templateUrl: './validation-error-summary.component.html',
    styleUrls: ['./validation-error-summary.component.scss'],
    standalone: false
})
export class ValidationErrorSummaryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() form: UntypedFormGroup;
  // INC-289: lets a caller whose fields are split across several FormGroups
  // (e.g. profile-details.component.ts after its identity/contact fields
  // moved into IdentitySectionFormComponent/ContactSectionFormComponent)
  // still get ONE combined error summary, without re-merging the groups
  // into a single control tree.
  @Input() additionalForms: UntypedFormGroup[] = [];
  @Input() formName: string;
  @Input() errorFilterFn: () => void;

  errors: {
    name: string;
    message: string;
  }[] = [];

  filteredErrors: BehaviorSubject<
    {
      name: string;
      message: string;
    }[]
  > = new BehaviorSubject([]);

  private statusSubscription?: Subscription;

  constructor(
    private translateService: TranslateService,
    private invalidControlScrollService: InvalidControlScrollService,
  ) {}

  ngOnInit() {
    if (this.form instanceof UntypedFormGroup === false) {
      throw new Error('Must supply validation summary with a UntypedFormGroup.');
    }

    this.wireSubscriptions();
  }

  ngOnChanges(changes: SimpleChanges) {
    // additionalForms can arrive after ngOnInit (e.g. a ViewChild-backed
    // form that isn't available until its own *ngIf view is created) —
    // re-wire whenever the set of forms we're summarising changes.
    if (changes.additionalForms && !changes.additionalForms.firstChange && this.form) {
      this.wireSubscriptions();
    }
  }

  ngOnDestroy() {
    this.statusSubscription?.unsubscribe();
  }

  private wireSubscriptions() {
    this.statusSubscription?.unsubscribe();

    const forms = [this.form, ...(this.additionalForms ?? [])].filter(
      (formGroup): formGroup is UntypedFormGroup => !!formGroup,
    );

    this.statusSubscription = merge(...forms.map((formGroup) => formGroup.statusChanges)).subscribe(
      () => {
        setTimeout(() => {
          this.resetErrorMessages();
          this.generateErrorMessages(forms);
        }, 1);
      },
    );
  }

  updateFilteredErrors() {
    const filteredErrors = this.errorFilterFn
      ? this.errors.filter(this.errorFilterFn)
      : this.errors;

    this.filteredErrors.next(filteredErrors);
  }

  resetErrorMessages() {
    this.errors.length = 0;
  }

  generateErrorMessages(formGroups: FormGroup[]) {
    formGroups.forEach((formGroup) => {
      Object.keys(formGroup.controls).forEach((controlName) => {
        let inputName = this.formName
          ? this.translateService.instant(`forms.${this.formName}.${controlName}`)
          : controlName;

        let control = formGroup.controls[controlName];
        let errors = control.errors;

        if (errors === null || Object.keys(errors).length === 0) {
          return;
        }
        // Handle the 'required' case
        if (errors.required) {
          this.errors.push({
            name: controlName,
            message: `${inputName} is required`,
          });
        }
        // Handle 'minlength' case
        if (errors.minlength) {
          this.errors.push({
            name: controlName,
            message: `${inputName} minimum length is ${errors.minlength.requiredLength}`,
          });
        }
        // Handle custom messages.
        if (errors.message) {
          this.errors.push({
            name: controlName,
            message: `${inputName} ${errors.message}`,
          });
        }

        this.updateFilteredErrors();
      });
    });
  }

  scrollToFormControl(formControlName: string) {
    this.invalidControlScrollService.scrollToFormControl(formControlName);
  }
}
